import React, { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useMemo, useRef, useState } from "react";
import styled from "styled-components/macro";
import { App } from "../../../app";
import { ResourceStrings } from "../../../ResourceStrings";
import MarkdownEditor, { MarkdownEditorHandles } from "../../shared/MarkdownEditor";
import Button from "../../shared/Button";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import Panel from "../../shared/Panel";
import Flex from "../../shared/Flex";
import { turndownService } from "../../..";
import { isHtmlText } from "../../../utils/tools";
import { Person } from "../../shared/PersonPicker";
import * as Actions from "../../../store/actions";
import { NodeVisibility } from "../../../store/actions";
import Draft, { DraftHandles } from "../../shared/Draft";
import ScreenshotEditor from "../Submission/ScreenshotEditor";
import ActionRecorder from "../Submission/ActionRecorder";
import AttachmentList from "../Submission/AttachmentList";
import { ReportType } from "../../../store/appState";
import { platformActions } from "../../../communication/Communication";
import { hasItems } from "../../../utils/collections";
import * as _ from "lodash";
import { Checkbox } from "@fluentui/react/lib/Checkbox";
import { codeOfConduct, dataWeCollect, getPrivacyStatementUrl } from "../../../constants/urls";
import VisibilityTransition from "../../shared/VisibilityTransition";
import { forMinWidth } from "../../../theme/breakpoints";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootState";
import { Icon } from "@fluentui/react/lib/Icon";
import { instance } from "../../../config/serverConfig";

interface NodeVisibilityDetail {
  key: NodeVisibility;
  text: string;
}

const nodeVisibilities: NodeVisibilityDetail[] = [
  { key: NodeVisibility.all, text: "Public" },
  { key: NodeVisibility.msonly, text: "Microsoft Only" },
  { key: NodeVisibility.privateToCustomer, text: "Private to customer (includes Microsoft)" },
]

export enum NodeType {
  comment = "comment",
  solution = "solution",
  workaround = "workaround",
}

interface NodeTypeDetail {
  key: NodeType;
  text: string;
}

const nodeTypes: NodeTypeDetail[] = [
  { key: NodeType.comment, text: "Comment" },
  { key: NodeType.solution, text: "Solution" },
  { key: NodeType.workaround, text: "Workaround" },
]

const draftFieldName = "value";

export interface RichNodeEditorData {
  text: string;
  nodeType: NodeType;
  visibility?: NodeVisibility;
  includeNativeAttachments: boolean;
}

const getPropForNodeType = <R,>(prop: R | ((nodeType: NodeType) => R), nodeType: NodeType, defaultValue: R): R => {
  if (!prop) {
    return defaultValue;
  }
  if (_.isFunction(prop)) {
    return prop(nodeType);
  }
  return prop;
};

export interface RichNodeEditorHandles {
  focus: () => void;
}

export interface RichNodeEditorProps {
  title?: string | JSX.Element;
  defaultNodeType?: NodeType;
  isNodeTypeReadonly?: boolean;
  canChooseNodeType?: boolean;
  allowedNodeTypes?: NodeType[];
  nodeTrackingId?: string;
  setNewNodeTrackingId?: (trackingId: string) => void;
  nodeAnswerHubId?: number;
  className?: string;
  canDismiss?: boolean;
  forbidAttachFiles?: boolean;
  reasonCantAttachFiles?: string;
  canChooseVisibility?: boolean;
  defaultVisibility?: NodeVisibility;
  placeHolderText?: string | ((nodeType: NodeType) => string);
  textPrefix?: (nodeType: NodeType) => string;
  guidelines?: string | JSX.Element | ((nodeType: NodeType) => string | JSX.Element);
  defaultText?: string;
  relevantPersons?: Person[];
  isSubmitting?: boolean;
  disabled?: boolean;
  draftKeyName?: string;
  draftLabel?: string;
  searchPersons?: (filter: string) => Person[];

  allowNativeAttachments?: boolean;
  canHideLogsAndRecordings?: boolean;   // e.g. we'll always show logs and recordings option for NMI but user can choose for normal comments
  logsAndRecordingsSelected?: boolean;

  required?: boolean;
  minChars?: number;
  maxChars?: number;
  validate?: (value: RichNodeEditorData) => string;
  onSubmit?: (value: RichNodeEditorData) => void;
  onDismiss?: () => void;
}

const RichNodeEditor: ForwardRefRenderFunction<RichNodeEditorHandles, RichNodeEditorProps> = (props: RichNodeEditorProps, ref): JSX.Element => {
  const [isDirty, setIsDirty] = useState(false);
  const [visibility, setVisibility] = useState<NodeVisibility>(props.defaultVisibility);
  const [logsAndRecordingsSelected, setLogsAndRecordingsSelected] = useState(!!props.logsAndRecordingsSelected);
  const [nodeType, setNodeType] = useState(props.defaultNodeType ?? null);
  const refMarkdownEditor = useRef<MarkdownEditorHandles>();
  const refDraft = useRef<DraftHandles>();
  const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (refDraft.current?.isVisible()) {
        refDraft.current?.focus();
      } else {
        refMarkdownEditor.current?.focus();
      }
    }
  }));

  const userEmail = useSelector((state: RootState) => state.appState.userEmail);
  const allowedVisibilities = useMemo(
    () => {
      if (App.isMicrosoftAccount(userEmail)) {
        return nodeVisibilities;
      } else {
        return nodeVisibilities.filter(x => x.key !== NodeVisibility.privateToCustomer);
      }
    }
    , [userEmail]
  );

  const [text, setText] = useState<string>(() => {
    let defaultText = props.defaultText ?? "";
    if (isHtmlText(defaultText)) {
      defaultText = turndownService.turndown(defaultText);
    }
    return defaultText;
  });

  const nativeProcessAvailable = !!platformActions.getNativeActions();

  const formatInfoHandler = (action: string): void => {
    Actions.setUsedFormatting(true);
    App.telemetry.postBusinessEvent("formatcommand-used", {
      command: action,
    });
  };

  const handleVisibilityChanged = (option: IChoiceGroupOption): void => {
    setVisibility(instance.isInternal ? NodeVisibility.msonly : option.key as NodeVisibility);
  }

  const handleNodeTypeChanged = (option: IChoiceGroupOption): void => {
    setNodeType(option.key as NodeType);
  }

  const draftValue = useMemo(() => {
    const data = new Map<string, string>();
    data.set(draftFieldName, text);
    return data;
  }, [text]);

  let isInvalid = false;
  let errors = "";
  if (props.required && !text) {
    isInvalid = true;
  }
  if (props.minChars && text && text.length < props.minChars) {
    isInvalid = true;
    errors += `${props.minChars - text.length} characters needed`;
  }
  if (props.maxChars && text && text.length > props.maxChars) {
    isInvalid = true;
    errors += `Exceeded by ${text.length - props.maxChars} characters`;
  }
  if (!nodeType) {
    isInvalid = true;
  }
  if (nodeType === NodeType.comment && props.canChooseVisibility && !visibility) {  // When adding a new comment visibility needs to be selected
    isInvalid = true;
  }
  if (props.validate) {
    const validationResult = props.validate({ text, visibility, nodeType, includeNativeAttachments: props.allowNativeAttachments && logsAndRecordingsSelected });
    if (validationResult) {
      isInvalid = true;
      errors += validationResult;
    }
  }
  if (!isInvalid && props.maxChars && text) {
    errors = `${props.maxChars - text.length} characters left`;
  }

  const isRecordingEnabled: boolean = !App.settings.isRecordingDisabled;
  const reportType = ReportType.provideInfoComment;

  const allowedNodeTypes = hasItems(props.allowedNodeTypes)
    ? nodeTypes.map(x => ({ ...x, disabled: !props.allowedNodeTypes.some(y => y === x.key) }))
    : nodeTypes;

  const placeHolderText = useMemo(() => getPropForNodeType(props.placeHolderText, nodeType, ""), [props.placeHolderText, nodeType]);
  const guidelines = useMemo(() => getPropForNodeType(props.guidelines, nodeType, null), [props.guidelines, nodeType]);
  const title = useMemo(() => getPropForNodeType(props.title, nodeType, ""), [props.title, nodeType]);

  const beforePreview = (markdown: string, nodeType: NodeType): string => {
    if (props.textPrefix) {
      return `${props.textPrefix(nodeType)}${markdown}`;
    }
  }

  const canChooseVisibility = props.canChooseVisibility && (!nodeType || nodeType === "comment");
  const allowNativeAttachments = props.allowNativeAttachments && nodeType === "comment";

  const handleSubmit = (): void => props.onSubmit({
    text, visibility, nodeType, includeNativeAttachments: props.allowNativeAttachments && logsAndRecordingsSelected
  });

  if (isInReadOnlyMode){
    return <div>Editing is disabled because website is in readonly mode.</div>
  }

  return <Wrapper column densityVer="normal" className={props.className}>
    {title && <div className="editor-title">
      <h1>{title}</h1>
      {!instance.isInternal && <a href={codeOfConduct} className="code-of-conduct" target="_blank" rel="noopener noreferrer">Community Code of Conduct</a>}
    </div>}

    {props.draftKeyName && <Draft
      canShow={true}
      ref={refDraft}
      name={props.draftKeyName}
      label={props.draftLabel}
      trackingId={props.nodeTrackingId}
      fieldNames={[draftFieldName]}
      setNewNodeTrackingId={props.setNewNodeTrackingId}
      data={draftValue}
      onDraftLoaded={(data: Map<string, string>) => {
        setText(data.get(draftFieldName));
        refMarkdownEditor.current?.focus();
      }}
      onDraftDeleted={() => {
        refMarkdownEditor.current?.focus();
      }}
    />}

    {props.canChooseNodeType && (
      <ViewableBy densityHor="normal" alignItems="center">
        <span>This is a</span>
        <ChoiceGroup
          options={allowedNodeTypes}
          selectedKey={nodeType}
          disabled={props.disabled || props.isSubmitting}
          onChange={(e, option) => handleNodeTypeChanged(option)}
        />
      </ViewableBy>
    )}

    <MarkdownEditorWrapper>
      <MarkdownEditor
        ref={refMarkdownEditor}
        text={text}
        nodeTrackingId={props.nodeTrackingId}
        nodeAnswerHubId={props.nodeAnswerHubId}
        ariaLabel={ResourceStrings.description()}
        forbidAttachFiles={props.forbidAttachFiles}
        reasonCantAttachFiles={props.reasonCantAttachFiles}
        ariaRequired={true}
        placeHolder={placeHolderText}
        disabled={isInReadOnlyMode || props.disabled || props.isSubmitting}
        relevantPersons={props.relevantPersons}
        searchPersons={props.searchPersons}
        formatInfoHandler={formatInfoHandler}
        beforePreview={(markdown: string) => beforePreview(markdown, nodeType)}
        textChanged={(value: string) => {
          setText(value);
          setIsDirty(true);
        }}
        isInvalid={isInvalid}
        notificationText={errors}
      />
    </MarkdownEditorWrapper>

    {canChooseVisibility && (
      <ViewableBy densityHor="normal" alignItems="center">
        <span className="required-field">Privacy settings</span>
        <ChoiceGroup
          options={allowedVisibilities}
          selectedKey={visibility}
          disabled={props.disabled || props.isSubmitting}
          onChange={(e, option) => handleVisibilityChanged(option)}
        />

        {visibility === NodeVisibility.all && (
          <span className="public-visibility-warning">
            <Icon iconName="Warning" />
            <span>Do not include personal information</span>
          </span>
        )}

        {(visibility === NodeVisibility.msonly || visibility === NodeVisibility.privateToCustomer) && (
          <span className="public-visibility-info">
            <span>Your comment will be private to Microsoft</span>
          </span>
        )}
      </ViewableBy>
    )}

    {/* Show message when adding/modifying solution or when modifying comment which isn't private. Other cases are handled separately above.  */}
    {(!canChooseVisibility && visibility !== NodeVisibility.msonly && visibility !== NodeVisibility.privateToCustomer) && (
      <ViewableBy densityHor="normal" alignItems="center">
        <span className="public-visibility-warning">
          <Icon iconName="Warning" />
          <span>Do not include personal information</span>
        </span>
      </ViewableBy>
    )}

    <VisibilityTransition showIf={!!guidelines}>
      <Guidelines>{guidelines}</Guidelines>
    </VisibilityTransition>

    {allowNativeAttachments && (
      <Panel className="attach-logs-container">
        <div className="attach-logs-btn-container">
          <Checkbox
            checked={logsAndRecordingsSelected}
            label="Attach system logs and recordings"
            disabled={isInReadOnlyMode || props.disabled || props.isSubmitting}
            onChange={(e) => setLogsAndRecordingsSelected(x => !x)}
          />
          <span>These files are always private</span>
        </div>

        {allowNativeAttachments && logsAndRecordingsSelected && <>
          <div className="attach-logs-body">
            {/* @TODO: Update this message probably with a link to page which explains the VS-connected scenario */}
            {allowNativeAttachments && logsAndRecordingsSelected && !nativeProcessAvailable && (
              <div className="native-process-needed">{"To attach system logs and add recordings please launch this website from 'Help' > 'Send Feedback' > 'My Feedback' menu in Visual Studio."}</div>
            )}

            <VisibilityTransition showIf={!isInReadOnlyMode && allowNativeAttachments && logsAndRecordingsSelected && nativeProcessAvailable}>
              <NativeAttachmentsContainer>
                <Expandable><ScreenshotEditor reportType={reportType} disabled={props.disabled || props.isSubmitting} /></Expandable>
                {isRecordingEnabled && <Expandable><ActionRecorder reportType={reportType} disabled={props.disabled || props.isSubmitting} forNarrowContainer /></Expandable>}
                <Expandable><AttachmentList reportType={reportType} disabled={props.disabled || props.isSubmitting} /></Expandable>
                <DataCollectionPolicy>
                  <a href={dataWeCollect} target="_blank" rel="noopener noreferrer">{ResourceStrings.dataWeCollect()}</a>. Your attachments will be private to Microsoft. Your comment will be private to Microsoft unless you select Public.
                  <br />
                  See the <a href={getPrivacyStatementUrl()} target="_blank" rel="noopener noreferrer">{ResourceStrings.msftPrivacyStatement()}</a>.
                </DataCollectionPolicy>
              </NativeAttachmentsContainer>
            </VisibilityTransition>
          </div>
        </>}
      </Panel>
    )}

    <Flex densityHor="normal" className="bottom-buttons">
      <Button
        type="submit"
        showSpinner={props.isSubmitting}
        disabled={isInReadOnlyMode || isInvalid || !isDirty || props.disabled || props.isSubmitting}
        onClick={handleSubmit}
      >
        Submit
      </Button>
      {props.canDismiss && (
        <Button
          type="button"
          buttonType="normal"
          disabled={props.disabled || props.isSubmitting}
          onClick={() => props.onDismiss()}
        >
          Cancel
        </Button>
      )}
    </Flex>
  </Wrapper>
}

export default forwardRef(RichNodeEditor);

const Wrapper = styled(Flex)`
  position: relative;
  margin-top: .5rem;
  padding: .2rem;

  .markdown-toolbar {
      border-bottom: solid 1px var(--color-on-surface-lightest);
  }

  .attach-logs-container {
    border: solid 1px var(--color-on-surface-light);
  }

  .attach-logs-btn-container {
    box-shadow: none;

    > span {
      display: block;
      margin-left: 2rem;
    }

    ${forMinWidth.md`
      > span {
        display: inline;
        margin-left: 0;
      }
    `}

    .ms-Checkbox.ms-Checkbox {
      display: inline-block;
      padding: 0.2rem 0.4rem;
      margin: .8rem 1.2rem;
      min-height: auto;

      .ms-Checkbox-label {
      align-items: center;
      }

      .ms-Checkbox-checkbox {
      border-color: var(--color-on-surface-link);
        height: 1.6rem;
        width: 1.6rem;
      }

      .ms-Checkbox-text {
      font-size: 1.4rem;
        font-weight: 600;
        color: var(--color-on-surface-link);
      }
    }

    span {
      color: var(--color-on-surface-light);
      font-size: 1.4rem;
    }
  }

  .editor-title {
    .code-of-conduct {
      font-size: 1.5rem;
    }

    ${forMinWidth.sm`
      h1 {
        display: inline-block;
      }

      .code-of-conduct {
        margin-left: 1.4rem;
      }
    `}
  }

  .attach-logs-body {
    padding: 2rem 2.2rem 1rem;
  }

  .native-process-needed {
    font-size: 1.5rem;
  }

  .bottom-buttons {
    margin-top: 3rem;
  }
`;

const MarkdownEditorWrapper = styled(Panel)`
  border: solid 1px var(--color-surface-dark1);

  .detail {
      margin: 0;
  }
`;

const ViewableBy = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;

  > * + * {
    margin-top: 1rem;
  }

  ${forMinWidth.lg`
    flex-direction: row;
    align-items: baseline;

    > * + * {
      margin-top: 0;
    }
  `}

  .public-visibility-warning {
    display: flex;
    color: var(--color-warning);
    font-weight: 600;
    font-size: 1.4rem;

    i {
      margin-right: 0.4rem;
    }
  }

  .public-visibility-info {
      font-size: 1.5rem;
      color: var(--color-on-surface-light);
    }

  .ms-ChoiceFieldGroup-flexContainer {
    display: flex;
    flex-direction: column;

    .ms-ChoiceField {
      min-height: 2.6rem;
    }

    .ms-ChoiceField-field {
      font-size: var(--font-size-default);
      white-space: nowrap;
    }

    .ms-ChoiceFieldLabel {
      padding-left: 2.6rem;
    }

    .ms-ChoiceField-field::before {
      width: 2rem;
      height: 2rem;
    }

    .ms-ChoiceField-field::after, .ms-ChoiceField-field:hover::after {
      width: 1rem;
      height: 1rem;
      top: .5rem;
      left: .5rem;
      border-width: .5rem;
    }

    ${forMinWidth.md`
      flex-direction: row;

      > * + * {
        margin-left: 1.5rem;
      }
    `}
  }
`;

const Guidelines = styled.div`
  background-color: #FFF7DD;
  padding: 2rem 3rem;
  line-height: 200%;
  border-radius: .2rem;
`;

const Expandable = styled(Panel)`
  box-shadow: none;
  border: solid 1px var(--color-on-surface-light);

  .accordion-header {
      height: 2rem;

    span {
      font-size: 1.4rem;
    }
  }
`;

const NativeAttachmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;

  >* + * {
      margin-top: 2rem;
  }
`;
const DataCollectionPolicy = styled.div`
  font-size: 1.4rem;

  a {
      font-weight: 600;
  }
`;
